/**
 * Utility for sanitizing commander and theme names for URLs
 * Handles special characters, diacritics, and other edge cases
 */

/**
 * Normalize diacritics/accented characters to their ASCII equivalents
 * e.g., "û" → "u", "é" → "e", "ñ" → "n"
 * 
 * Uses Unicode NFD normalization to decompose accented characters,
 * then removes the combining diacritical marks
 */
export const normalizeDiacritics = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Sanitize a commander name for use in URLs
 * Matches the EDHREC URL format: lowercase, hyphens, no special chars
 * 
 * @param {string} name - The commander name (e.g., "Lord of the Nazgûl")
 * @returns {string} - Sanitized name (e.g., "lord-of-the-nazgul")
 */
export const sanitizeCommanderName = (name) => {
  if (!name) return '';
  
  // Handle double-faced cards - use only the front face name
  const frontFaceName = name.split(' // ')[0];
  
  return frontFaceName
    .toLowerCase()
    // Normalize diacritics first (û → u, é → e, etc.)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Remove all special characters except spaces and hyphens
    .replace(/[^a-z0-9\s-]/g, '')
    // Replace spaces with hyphens
    .replace(/\s+/g, '-');
};

/**
 * Sanitize a theme name for use in URLs
 * Handles special cases like +1/+1 counters
 * 
 * @param {string} theme - The theme name (e.g., "+1/+1 Counters")
 * @returns {string} - Sanitized theme name (e.g., "p1-p1-counters")
 */
export const sanitizeThemeName = (theme) => {
  if (!theme) return '';
  
  return theme
    .toLowerCase()
    // Handle counter themes before normalization
    .replace(/\+1\/\+1/g, 'p1-p1')
    .replace(/-1\/-1/g, 'm1-m1')
    // Normalize diacritics
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Remove all special characters except spaces and hyphens
    .replace(/[^a-z0-9\s-]/g, '')
    // Replace spaces with hyphens
    .replace(/\s+/g, '-');
};

export default {
  normalizeDiacritics,
  sanitizeCommanderName,
  sanitizeThemeName
};
